import {ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, input} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {AksjeeierEnkelModell, Aksjeeiertype, EnkelGruppeMedlemModell, Epost, GruppeMedlemListeModell, GruppeModell, MedlemId, MedlemsRolleModell, MøtedeltakerModell, MøtedeltakerSpesifikasjon} from "../../../../../felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatButtonModule} from "@angular/material/button";
import {FormaterProsentPipe} from "felles-ng2/pipes/FormaterProsentPipe";
import {MatOptionModule, MatOptionSelectionChange} from "@angular/material/core";
import {MatSelectModule} from "@angular/material/select";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {nyUuid} from "felles-ng2/støttefunksjoner/uuidGenerator";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {trimTilNull} from "felles-ng2/støttefunksjoner/strenger";
import {TranslateModule} from "@ngx-translate/core";
import {MedlemInvitasjonSkjemaKomponent} from "web-ng2/moduler/medlem/komponenter/medlem-invitasjon-skjema/medlem-invitasjon-skjema";
import {medlemmerAktive, primærMedlemsrolle} from "felles-ng2/modell/medlemModellHjelpere";
import {toSignal} from "@angular/core/rxjs-interop";
import {map} from "rxjs/operators";
import {oppdaterGuiVedEndringIFormArrayLengde, skjemaendringerSomObserverbar} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {aksjeeierHarBeholdning} from "felles-ng2/modell/aksjeeierModellHjelpere";
import {finnDeltakerSomRepresentererAksjeeier} from "felles-ng2/modell/møtedeltakerModellHjelpere";

const debug = initDebugger('GeneralforsamlingRedigerRepresentasjonerKontroller');

export type GfRedigerRepresentasjonerSkjemaType = ReturnType<typeof GeneralforsamlingRedigerRepresentasjonerKontroller.opprettSkjema>;
export type GfRedigerRepresentasjonerSkjemaVerdier = GfRedigerRepresentasjonerSkjemaType['value'];
export type AksjeeierRepresentasjonSkjemaType = ReturnType<typeof GeneralforsamlingRedigerRepresentasjonerKontroller.opprettSkjemarad>;
export type AksjeeierRepresentasjonSkjemaVerdier = AksjeeierRepresentasjonSkjemaType['value'];
export type MedlemInvitasjonsskjemaForAksjeeierSkjemaType = ReturnType<typeof GeneralforsamlingRedigerRepresentasjonerKontroller.nyDisabletInvitasjonForAksjeeierSkjemarad>

@Component({
    selector: 'generalforsamling-rediger-representasjoner',
    templateUrl: './generalforsamling-rediger-representasjoner.html',
    styleUrl: './generalforsamling-rediger-representasjoner.scss',
    imports: [
        ReactiveFormsModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatTooltipModule,
        MatButtonModule,
        TipsKomponent,
        FormaterProsentPipe,
        MatOptionModule,
        MatSelectModule,
        MatSlideToggleModule,
        InlineMeldingKomponent,
        TranslateModule,
        MedlemInvitasjonSkjemaKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GeneralforsamlingRedigerRepresentasjonerKontroller {

    /** Oppretter nytt tomt skjema */
    static opprettSkjema() {
        return new FormArray<AksjeeierRepresentasjonSkjemaType>([])
    }

    /** Nullstiller angitt skjema og populerer det med angitte aksjeeiere (utelater aksjeeier uten beholdning).
     * Representerende medlem hentes fra angitt deltakere liste dersom denne er angitt, ellers fra aksjeeier.representertAv */
    static repopulerSkjema(skjema: GfRedigerRepresentasjonerSkjemaType, aksjeeiere: AksjeeierEnkelModell[], deltakere?: MøtedeltakerModell[]) {
        skjema.clear({emitEvent: false});
        aksjeeiere
          // utelat aksjeeiere som ikke har beholdning nå. mulig dette burde være på møtedato.
          .filter(aksjeeier => aksjeeierHarBeholdning(aksjeeier))
          .map(aksjeeier => {
              let representant = erIkkeNil(deltakere) ?
                finnDeltakerSomRepresentererAksjeeier(deltakere, aksjeeier)?.medlem :
                aksjeeier.representertAv;
              // etter migrering har vi hatt medlemmer som er anonymisert som har kommet inn i GF møter. De skal ikke kunne delta i møte, det oppstår feil av dette.
              // redigering av møte med anonymisert deltaker fjerner deltaker - dette er som regel greit.
              if (representant?.anonymisert) representant = null;
              return GeneralforsamlingRedigerRepresentasjonerKontroller.opprettSkjemarad(aksjeeier, representant ?? null);
          })
          .forEach(skjemarad => skjema.push(skjemarad, {emitEvent: false}));
        //først nå emitter vi en valueChanges event (foregående clear og push kall har ikke ført til eventer)
        skjema.updateValueAndValidity();
    }

    static opprettSkjemarad(aksjeeier: AksjeeierEnkelModell, medlem: EnkelGruppeMedlemModell | null) {
        return new FormGroup({
            aksjeeier: new FormControl<AksjeeierEnkelModell>(aksjeeier, {nonNullable: true}),
            /** Eksisterende medlem satt til å representere aksjeeier. '{}' dersom nyInvitasjonForAksjeeier er enablet, og null når hverken medlem er valgt eller yInvitasjonForAksjeeier er enablet */
            medlem: new FormControl<EnkelGruppeMedlemModell | null>(medlem, {nonNullable: false}),
            /** Potensiell gruppemedlem-invitasjon som representerer aksjeeier.
             * Disablet med mindre ny invitasjon skal registreres for denne aksjeeieren.
             * Merk at skjema.value vil ikke inneholde nyInvitasjonForAksjeeier dersom feltet er disablet.
             * Disablet dersom medlem er satt. Se {@link #initNyInvitasjonSkjema} */
            nyInvitasjonForAksjeeier: GeneralforsamlingRedigerRepresentasjonerKontroller.nyDisabletInvitasjonForAksjeeierSkjemarad(),
            /** Sann dersom bruker har valgt å registrere ny gruppeinvitasjon som representant for aksjeeier.*/
            erNyInvitasjon: new FormControl(false, {nonNullable: true}),
        });
    }

    /** Returnerer liste med informasjon om deltakere og hvilke aksjonærer de representerer.
     * For nye invitasjoner sørger vi for at duplikate epost-adresser reduseres til unike innslag,
     * dvs at [{medlemInvitasjon.epost:kai@noe, aksjeeierId:a}, {medlemInvitasjon.epost:kai@noe, aksjeeierId:b}] reduseres til
     * [{medlemInvitasjon.epost:kai@noe, representerteAksjeeiere:[a, b]}].
     * Forventer at angitt skjemaverdier er hentet fra skjema opprettet med {@link GeneralforsamlingRedigerRepresentasjonerKontroller#opprettSkjema} */
    static somMøtedeltakerSpesifikasjoner(skjemaverdier: GfRedigerRepresentasjonerSkjemaVerdier): MøtedeltakerSpesifikasjon[] {
        const eksisterendeMedlemmer = skjemaverdier
          .filter(rad => !rad.erNyInvitasjon)
          .filter(rad => erIkkeNil(rad.medlem))
          .reduce((gruppert, medlemOgAksjeeier) => {
                const medlemId = medlemOgAksjeeier.medlem!.id;
                const innslag = gruppert[medlemId] || (gruppert[medlemId] = {medlemId: medlemId, medlemInvitasjon: null, representerteAksjeeiere: []});
                innslag.representerteAksjeeiere!.push(medlemOgAksjeeier.aksjeeier!.id);
                return gruppert;
            },
            {} as Record<MedlemId, MøtedeltakerSpesifikasjon>);

        //for aksjeeiere ikke tilknyttet eksisterende medlem kan bruker registrere ny gruppeinvitasjon
        const invitasjoner = skjemaverdier
          .filter(rad => rad.erNyInvitasjon)
          //for invitasjoner bruker vi angitt epost som nøkkel slik at invitasjoner med lik epostadresse slås sammen til én
          .reduce((gruppert, invitasjonOgAksjeeier) => {
                const medlemEpost = invitasjonOgAksjeeier.nyInvitasjonForAksjeeier!.nyInvitasjonSkjema!.epost!;
                const innslag = gruppert[medlemEpost] || (gruppert[medlemEpost] = {medlemId: null, medlemInvitasjon: {id: invitasjonOgAksjeeier.nyInvitasjonForAksjeeier?.nyInvitasjonSkjema!.id!, epost: medlemEpost, navn: null, mobilnummer: null, rolleId: null, skalSignere: false}, representerteAksjeeiere: []});
                innslag.representerteAksjeeiere!.push(invitasjonOgAksjeeier.aksjeeier!.id);
                //første rad med ikke-null verdi vinner
                innslag.medlemInvitasjon!.navn ??= trimTilNull(invitasjonOgAksjeeier.nyInvitasjonForAksjeeier!.nyInvitasjonSkjema!.navn);
                innslag.medlemInvitasjon!.mobilnummer ??= trimTilNull(invitasjonOgAksjeeier.nyInvitasjonForAksjeeier!.nyInvitasjonSkjema!.mobilnummer);
                innslag.medlemInvitasjon!.rolleId ??= trimTilNull(invitasjonOgAksjeeier.nyInvitasjonForAksjeeier!.nyInvitasjonSkjema!.rolle?.id);
                // ikke overstyr til false hvis den er satt til true.
                if (innslag.medlemInvitasjon!.skalSignere === false)
                    innslag.medlemInvitasjon!.skalSignere = erNil(invitasjonOgAksjeeier.nyInvitasjonForAksjeeier!.skalSignere) ? false : invitasjonOgAksjeeier.nyInvitasjonForAksjeeier!.skalSignere;
                return gruppert;
            },
            {} as Record<Epost, MøtedeltakerSpesifikasjon>);

        return [
            ...Object.values(eksisterendeMedlemmer),
            ...Object.values(invitasjoner)
        ]
    }

    static nyDisabletInvitasjonForAksjeeierSkjemarad() {
        let skjema = new FormGroup({
            //NB: navnet på denne kontrolleren må holdes i synk med navnet brukt i erInvitasjonForAksjeeierSkjema()
            nyInvitasjonSkjema: MedlemInvitasjonSkjemaKomponent.opprettSkjema(),
            skalSignere: new FormControl<boolean>(false, {nonNullable: true, validators: [Validators.required]})
        });
        skjema.disable();
        return skjema;
    }

    /** Sann dersom angitt skjema er av typen {@link MedlemInvitasjonsskjemaForAksjeeierSkjemaType}*/
    static erInvitasjonForAksjeeierSkjema(skjema: FormGroup | MedlemInvitasjonsskjemaForAksjeeierSkjemaType) {
        return skjema.contains('nyInvitasjonSkjema');
    }

    // GeneralforsamlingRedigerRepresentasjonerKontroller implementasjon

    skjema = input.required<GfRedigerRepresentasjonerSkjemaType>();
    gruppe = input.required<GruppeModell>();
    /** Medlemsrolle som skal brukes som standard rolle ved opprettelse av ny gruppeinvitasjon */
    standardAksjeeierRolle = input.required<MedlemsRolleModell | null>();

    aktiveMedlemmer = computed(() => medlemmerAktive(this.gruppe().alleMedlemmer));
    antallAksjeeiereMedRepresentantTildelt = toSignal(skjemaendringerSomObserverbar(this.skjema).pipe(
      map(rader => rader.filter(rad => rad.erNyInvitasjon ? erIkkeNil(rad.nyInvitasjonForAksjeeier) : erIkkeNil(rad.medlem))),
      map(raderMedRepresentantTildelt => raderMedRepresentantTildelt.length)
    ), {initialValue: 0});
    antallAksjeeiereUtenRepresentantTildelt = computed(() => this.skjema().length - this.antallAksjeeiereMedRepresentantTildelt());
    inneholderInvitasjoner = toSignal(skjemaendringerSomObserverbar(this.skjema).pipe(
      map(rader => rader.filter(rad => erIkkeNil(rad.nyInvitasjonForAksjeeier) || erIkkeNil(rad.nyInvitasjonForAksjeeier))),
      map(raderMedInvitasjon => raderMedInvitasjon.length)
    ), {initialValue: 0});

    constructor(cdRef: ChangeDetectorRef) {
        oppdaterGuiVedEndringIFormArrayLengde(this.skjema, cdRef);
    }

    erValgtMedlem(a: GruppeMedlemListeModell | null, b: GruppeMedlemListeModell | null) {
        if (a === b) return true;
        if (erNil(a) || erNil(b)) return false;
        return a.id === b.id;
    }

    aktiveRolleMedParentesRundt(medlem: GruppeMedlemListeModell) {
        const rolle = primærMedlemsrolle(medlem);
        if (rolle == null) return '';
        return '(' + rolle.tittel + ')';
    }

    visNyInvitasjonSkjema(rad: AksjeeierRepresentasjonSkjemaType, $event: MatOptionSelectionChange) {
        if ($event.isUserInput) //isUserInput er true dersom bruker valgte "Legg til nytt medlem", false dersom et annet element ble valgt
            this.initNyInvitasjonSkjema(rad);
        else
            this.kansellerNyInvitasjonSkjema(rad);
    }

    vedAvbrytNyMedlem(rad: AksjeeierRepresentasjonSkjemaType) {
        rad.controls.medlem.setValue(null);
        this.kansellerNyInvitasjonSkjema(rad);
    }

    // hjelpemetoder

    private initNyInvitasjonSkjema(rad: AksjeeierRepresentasjonSkjemaType) {
        aktiverNyInvitasjonSkjemaIAksjeeierRepresentasjon(rad, this.standardAksjeeierRolle());
    }

    private kansellerNyInvitasjonSkjema(rad: AksjeeierRepresentasjonSkjemaType) {
        rad.patchValue({erNyInvitasjon: false});
        rad.controls.nyInvitasjonForAksjeeier.disable();
    }
}

/** Returnerer sann dersom enten medlem eller ny invitasjon er satt i angitt representasjon. */
export function erRepresentantForAksjeeierAngitt(representasjon: AksjeeierRepresentasjonSkjemaVerdier): boolean {
    return representasjon.erNyInvitasjon ? erIkkeNil(representasjon.nyInvitasjonForAksjeeier) : erIkkeNil(representasjon.medlem);
}

/** Returnerer invitasjon.id dersom ny invitasjon er satt til å representere aksjeeier, ellers valgt medlem.id.
 * Forutsetter representant for aksjeeier er satt (til enten eksisterende medlem eller ny invitasjon). */
export function medlemIdEllerInvitasjonId(representasjon: AksjeeierRepresentasjonSkjemaVerdier): string {
    return representasjon.erNyInvitasjon ? representasjon.nyInvitasjonForAksjeeier!.nyInvitasjonSkjema!.id! : representasjon.medlem!.id;
}

/** Aktiverer nyInvitasjonForAksjeeier-skjema i angitt skjema */
//eksponert for testing
export function aktiverNyInvitasjonSkjemaIAksjeeierRepresentasjon(skjema: AksjeeierRepresentasjonSkjemaType, standardAksjeeierRolle: MedlemsRolleModell | null) {
    const aksjeeier = skjema.value.aksjeeier!;
    skjema.controls.nyInvitasjonForAksjeeier.enable();
    skjema.patchValue({
        erNyInvitasjon: true,
        nyInvitasjonForAksjeeier: {
            nyInvitasjonSkjema: {
                id: nyUuid(),
                navn: aksjeeier.type == Aksjeeiertype.person ? aksjeeier.navn : null, //bruker ikke organisasjonsnavn som standardverdi
                epost: aksjeeier.epost!,
                mobilnummer: null,
                rolle: standardAksjeeierRolle
            }
        }
    });
}
